import styled, {css} from "styled-components";
import PopupCloseButton from "@FRAMEWORK/ui/desktop/button/PopupCloseButton";
import {useRef, useState, useEffect, MutableRefObject} from "react";
import {PopupProps} from "@FRAMEWORK/ui/types/d";

// ─── 타입 ────────────────────────────────────────────────────────────────────

type Size = "small" | "regular" | "large" | "full";
type Animate = "basic" | "slideIn";

interface Props extends PopupProps {
	size?: Size;
	paddingTop?: boolean;
	show?: boolean;
	animate?: Animate;
	height?: number;
	close?: () => void;
}

// ─── 사이즈별 너비 (공통) ──────────────────────────────────────────────────

const sizeWidth: Record<string, string> = {
	small: "500px",
	regular: "800px",
	large: "990px",
};

const sizeWidthCss = (size?: string) =>
	sizeWidth[size ?? ""]
		? css`
				width: ${sizeWidth[size!]};
				min-width: ${sizeWidth[size!]};
			`
		: "";

// ─── 훅 ──────────────────────────────────────────────────────────────────────

const useResizeObserver = (ref: MutableRefObject<HTMLDivElement | null>) => {
	const [height, setHeight] = useState(0);

	useEffect(() => {
		const target = ref.current ?? document.querySelector<HTMLElement>(".popup_content");
		if (!target) return;

		const observer = new ResizeObserver(([entry]) => {
			setHeight(entry.target.clientHeight);
		});
		observer.observe(target);
		return () => observer.unobserve(target);
	}, [ref]);

	return height;
};

// ─── 스타일 ───────────────────────────────────────────────────────────────────

const Wrap = styled.div<{isScroll?: boolean; show?: boolean}>`
	position: fixed;
	inset: 0;
	z-index: 101;
	display: flex;
	align-items: ${({isScroll}) => (isScroll ? "flex-start" : "center")};
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 50px 0;
	background-color: rgba(0, 0, 0, 0.6);
	overflow-y: ${({isScroll}) => (isScroll ? "scroll" : "hidden")};
	box-sizing: border-box;
	opacity: 0;
	visibility: ${({show}) => (show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;
	animation: ${({show}) => (show ? css`fadeIn 0.3s ease forwards` : css`fadeOut 0.3s ease forwards`)};

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	@keyframes fadeOut {
		from {
			opacity: 1;
		}
		to {
			opacity: 0;
		}
	}
`;

const FullContent = styled.div<{size?: string}>`
	${({size}) =>
		size === "full" &&
		css`
			width: 100%;
			height: 100%;
			background-color: #fff;
			> div {
				margin: 0 auto;
			}
		`}
`;

const Content = styled.div<{
	size?: Size;
	top?: boolean;
	animate?: Animate;
	show?: boolean;
}>`
	position: relative;
	z-index: 105;
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	border-radius: 16px;
	padding-top: ${({top}) => (top ? "50px" : "0")};
	background-color: #fff;
	visibility: ${({show}) => (show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;

	${({animate, show}) =>
		animate === "slideIn" &&
		css`
			animation: ${show ? css`slideIn 0.3s cubic-bezier(0, 0, 0.2, 1)` : css`slideOut 0.3s cubic-bezier(0, 0, 0.2, 1)`};
		`}

	${({size}) =>
		size === "full"
			? css`
					height: 100%;
					padding-top: 0;
				`
			: sizeWidthCss(size)}

  @keyframes slideIn {
		from {
			transform: translateY(100%);
		}
		to {
			transform: translateY(0);
		}
	}
	@keyframes slideOut {
		from {
			transform: translateY(0);
		}
		to {
			transform: translateY(100%);
		}
	}

	@media print {
		display: block;
	}
`;

const Header = styled.div<{size?: Size; top?: boolean}>`
	display: flex;
	flex: 0 0 56px;
	border-bottom: 1px solid var(--border-color);
	border-radius: 16px 16px 0 0;
	background-color: var(--disable-bg-color);
	box-sizing: border-box;

	${({size}) =>
		size === "full"
			? css`
					position: sticky;
					top: -244px;
					width: 1200px;
					padding-right: 15px;
					margin: 0 auto;
					> div {
						margin: 0;
					}
				`
			: sizeWidthCss(size)}

	${({top}) =>
		top &&
		css`
			position: fixed;
			top: 0;
			height: 70px;
			background: #fff;
			z-index: 1;
		`}

  > div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex: 1 1 auto;
		margin: 20px 24px 8px;
	}

	@media print {
		display: none;
	}
`;

const Container = styled.div<{size?: string}>`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	overflow-y: auto;

	${({size}) =>
		size === "full"
			? css`
					padding: 0;
					margin-top: 0;
				`
			: css`
					padding: 32px 24px;
				`}

	@media print {
		padding: 0;
		overflow: visible;
	}
`;

const Title = styled.h2`
	display: -webkit-box;
	font-size: 20px;
	color: var(--title-color);
	font-family: "PT-semiBold";
	line-height: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: normal;
	word-wrap: break-word;
	word-break: break-all;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	line-clamp: 1;
`;

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────

const PopupContainerRound = ({
	title,
	children,
	size = "small",
	paddingTop = true,
	close,
	animate = "basic",
	show = true,
}: Props) => {
	const wrapRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [isScroll, setIsScroll] = useState(false);
	const [top, setTop] = useState(false);

	const contentHeight = useResizeObserver(contentRef);

	useEffect(() => {
		if (!wrapRef.current) return;
		setIsScroll(wrapRef.current.offsetHeight - 20 < contentHeight);
	}, [contentHeight]);

	useEffect(() => {
		if (!show) return;
		document.body.style.overflowY = "hidden";
		return () => {
			const openCount = document.querySelectorAll("[data-show='true']").length;
			if (openCount === 0) {
				document.body.style.removeProperty("overflow-y");
			}
		};
	}, [show]);

	const onScroll = () => {
		if (wrapRef.current) {
			setTop(wrapRef.current.scrollTop > 10);
		}
	};

	return (
		<Wrap ref={wrapRef} data-show={String(show)} show={show} isScroll={isScroll} onScroll={onScroll}>
			<FullContent size={size}>
				<Content className="popup_content" ref={contentRef} show={show} animate={animate} size={size} top={top}>
					<Header size={size} top={top}>
						<div>
							<Title>{title || "안내"}</Title>
							<PopupCloseButton onClose={close} />
						</div>
					</Header>
					<Container size={size}>{children}</Container>
				</Content>
			</FullContent>
		</Wrap>
	);
};

export default PopupContainerRound;
